import { ENVIRONMENT } from '../environment/environment.service';
import { HttpClient } from '@angular/common/http';
import { Environment, City, Response, Service, State, Country, EventTitle } from '@viso/core/models';
import { Inject, Injectable } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MasterService {
  private refreshSubject = new Subject<string>();
  refresh$ = this.refreshSubject.asObservable();

  triggerRefresh(eventType: string) {
    this.refreshSubject.next(eventType);
  }

  getRefreshObservable(eventType: string): Observable<void> {
    return this.refresh$.pipe(
      filter(event => event === eventType),
      map(() => void 0) // Convert string to void
    );
  }
  
  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {}

  getDeepLinkUrl(){
    return this.environment.deepLinkUrl;
  }

  getShareBaseUrl(){
    return this.environment.shareBaseUrl;
  }

  getServices(serviceId?) {
    return this.http
      .get(`${this.environment.API_URL}/auth/service-list/${serviceId ? serviceId : 0}`)
      .pipe(map((res: Response<Service[]>) => res.body));
  }

  getServicesAnynomous() {
    return this.http
      .get(`${this.environment.API_URL}/service-list`)
      .pipe(map((res: Response<Service[]>) => res.body));
  }

  getCitiesByStateId(stateId, user_type: string = 'client') {
    return this.http
      .get(`${this.environment.API_URL}/city?state=${stateId}`, { params: { user_type } })
      .pipe(map((res: Response<City[]>) => res.body));
  }

  getAllCountries(user_type: string = 'client') {
    return this.http
    .get(`${this.environment.API_URL}/country`, {
      params: { user_type }
    })
    .pipe(map((res: Response<Country[]>) => res.body));
  }

  getAllCities(state: string = '',service: string = '', user_type: string = 'client') {
    return this.http
      .get(`${this.environment.API_URL}/auth/city`, { params: { state,service,user_type } })
      .pipe(map((res: Response<City[]>) => res.body));
  }

  getAllCitiesAnynomous(q: string = '', user_type: string = 'client') {
    return this.http
      .get(`${this.environment.API_URL}/city`, { params: { q,user_type } })
      .pipe(map((res: Response<City[]>) => res.body));
  }

  getEventTitles() {
    return this.http
      .get(`${this.environment.API_URL}/event-titles`)
      .pipe(map((res: Response<EventTitle[]>) => res.body));
  }

  getEventTitleById(titleId): Observable<string | null> {
    return this.getEventTitles().pipe(
      map((titles: EventTitle[]) => {
        const eventTitle = titles.find(title => title.id === titleId);
        return eventTitle ? eventTitle.title : null;
      })
    );
  }

  getInvitationTitles() {
    return this.http
      .get(`${this.environment.API_URL}/invitation-titles`)
      .pipe(map((res: Response<EventTitle[]>) => res.body));
  }

  getInvitationTitleById(titleId): Observable<string | null> {
    return this.getInvitationTitles().pipe(
      map((titles: EventTitle[]) => {
        const eventTitle = titles.find(title => title.id === titleId);
        return eventTitle ? eventTitle.title : null;
      })
    );
  }

  getStateList(user_type: string = 'client') {
    return this.http
      .get(`${this.environment.API_URL}/state?country=101`, { params: { user_type } })
      .pipe(map((res: Response<State[]>) => res.body));
  }

  getCountryStateList(countryId, user_type: string = 'client') {
    return this.http
      .get(`${this.environment.API_URL}/state?country=${countryId}`, { params: { user_type } })
      .pipe(map((res: Response<State[]>) => res.body));
  }

  getConstant(keys: string) {
    return this.http
      .get(`${this.environment.API_URL}/getConstant?value=${keys}`)
      .pipe(map((res: Response<any>) => res.body));
  }

  saveOtherService(service) {
    return this.http.post<Response<Service>>(
      `${this.environment.API_URL}/auth/addMasterService`,
      service
    );
  }

  saveServiceImages(uuid: string, images: string) {
    return this.http
      .post(`${this.environment.API_URL}/vendor/deleteService`, {
        "uuid": uuid,
        "images": images,
      })
      .pipe(map((res: Response<any>) => res.body));
  }

  saveOtherLocation(location) {
    return this.http.post<Response<City>>(
      `${this.environment.API_URL}/auth/addMasterLocation`,
      location
    );
  }

  getFaq(type) {
    return this.http
      .get(`${this.environment.API_URL}/${type}/faq`)
      .pipe(map((res: Response<any>) => res.body));
  }

  getInvitationByCode(code) {
    return this.http.get(`${this.environment.API_URL}/invite/${code}`)
    .pipe(map((res: Response<any>) => res.body));
  }
}
